import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { Product } from '../../../domain/types';
import { ProductCard } from '../../../components/ProductCard';
import { HorizontalProductList } from '../../../components/HorizontalProductList';
import { RotatingBannerZone } from '../../../components/RotatingBannerZone';
import { ProductSkeleton } from '../../../components/skeletons/ProductSkeleton';
import { DISPLAY_LIMITS } from '../../../domain/constants';

// --- TYPES FOR FEED GENERATION ---
type FeedItem = 
  | { type: 'product_grid'; products: Product[] }
  | { type: 'injection_featured'; products: Product[] }
  | { type: 'injection_banner'; zone: 'catalog_inline'; index: number }
  | { type: 'injection_related'; title: string; products: Product[] }
  | { type: 'next_category'; categoryName: string; bgImage: string };

interface CatalogFeedProps {
  displayedProducts: Product[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  filter: string;
  stableSets: { featured: Product[]; impulse: Product[] };
  categories: Array<{ name: string; imageUrl?: string }>;
  mainScrollRef: React.RefObject<HTMLDivElement | null>;
  setFilter: (filter: string) => void;
  setSearchQuery: (query: string) => void;
  handleLoadMore: () => void;
  isFavoritesView: boolean;
  hasFavorites: boolean;
  searchQuery: string;
  crossCategoryMatches: Product[];
  allProducts: Product[];
  handleSelectFilter: (category: string) => void;
  handleShowAllCatalog: () => void;
  handleNavigateToProduct: (productId: string) => void;
}

const RELATED_CATEGORIES: Record<string, string> = {
  'Tecnología': 'Herramientas',
  'Hogar': 'Artesanías',
  'Moda': 'Deportes',
  'Mascotas': 'BARF',
  'Comidas': 'Bebidas',
  'Bebidas': 'Comidas',
  'Herramientas': 'Tecnología',
  'Artesanías': 'Hogar',
  'Deportes': 'Moda',
  'Juguetes': 'Tecnología',
  'Librería': 'Hogar',
  'Autos': 'Herramientas',
  'BARF': 'Mascotas',
  'Importados': 'Comidas',
  'Usados': 'Tecnología'
};

export const CatalogFeed: React.FC<CatalogFeedProps> = React.memo(({
  displayedProducts,
  loading,
  loadingMore,
  hasMore,
  filter,
  stableSets,
  categories,
  mainScrollRef,
  setFilter,
  setSearchQuery,
  handleLoadMore,
  isFavoritesView,
  hasFavorites,
  searchQuery,
  crossCategoryMatches,
  allProducts,
  handleSelectFilter,
  handleShowAllCatalog,
  handleNavigateToProduct
}) => {
  const navigate = useNavigate();

  // Derived Data for UI Injections
  const relatedCategorySet = useMemo(() => {
    if (filter === 'Todos') return allProducts.slice(0, DISPLAY_LIMITS.RELATED_CATEGORY);
    const relatedCatName = RELATED_CATEGORIES[filter];
    return allProducts.filter(p => p.category === relatedCatName).slice(0, DISPLAY_LIMITS.RELATED_CATEGORY);
  }, [allProducts, filter]);

  // --- FEED GENERATION LOGIC ---
  const feedItems = useMemo<FeedItem[]>(() => {
    if (loading || displayedProducts.length === 0) return [];

    const feed: FeedItem[] = [];
    const CHUNK_SIZE = 12;

    for (let i = 0; i < displayedProducts.length; i += CHUNK_SIZE) {
      const chunk = displayedProducts.slice(i, i + CHUNK_SIZE);
      feed.push({ type: 'product_grid', products: chunk });

      const injectionIndex = (i / CHUNK_SIZE) + 1;

      if (injectionIndex === 1 && stableSets.featured.length > 0) {
        feed.push({ type: 'injection_featured', products: stableSets.featured.slice(0, DISPLAY_LIMITS.IMPULSE_PRODUCTS_COUNT) });
      } else if (injectionIndex === 2) {
        feed.push({ type: 'injection_banner', zone: 'catalog_inline', index: 1 });
      } else if (injectionIndex === 3) {
        const title = filter === 'Todos' ? 'Oportunidades Flash' : `Quizás te interese: ${RELATED_CATEGORIES[filter] || 'Ofertas'}`;
        const prods = filter === 'Todos' ? stableSets.impulse.slice(0, DISPLAY_LIMITS.IMPULSE_PRODUCTS_COUNT) : relatedCategorySet;
        if (prods.length > 0) feed.push({ type: 'injection_related', title, products: prods });
      }
    }

    if (!hasMore && filter !== 'Todos') {
      const relatedName = RELATED_CATEGORIES[filter] || 'Todos';
      const relatedCat = categories.find(c => c.name === relatedName);
      if (relatedCat || relatedName === 'Todos') {
        const bgImage = relatedCat?.imageUrl || 'https://images.unsplash.com/photo-1472851294608-4151713425a5?auto=format&fit=crop&q=80&w=800';
        feed.push({ type: 'next_category', categoryName: relatedName, bgImage });
      }
    }

    return feed;
  }, [displayedProducts, loading, filter, stableSets, relatedCategorySet, categories, hasMore]);

  // --- RENDER HELPERS ---
  const renderFeedItem = useCallback((item: FeedItem, index: number) => {
    switch (item.type) {
      case 'product_grid':
        return (
          <div key={`grid-${index}`} className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 animate-fade-in">
            {item.products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        );
      case 'injection_featured':
        return (
          <div key={`feat-${index}`} className="py-4 -mx-4 mb-4 bg-gray-50/50 dark:bg-white/5 border-y border-black/5 dark:border-white/5">
            <HorizontalProductList 
              title="Destacados del Mes" 
              subtitle="Selección especial de AllTuc"
              products={item.products}
              icon="star"
            />
          </div>
        );
      case 'injection_banner':
        return (
          <div key={`banner-${index}`} className="py-6 mb-4">
            <RotatingBannerZone zone="catalog_inline" className="shadow-xl" />
          </div>
        );
      case 'injection_related':
        return (
          <div key={`rel-${index}`} className="py-4 -mx-4 mb-4">
            <HorizontalProductList 
              title={item.title}
              subtitle="Complementa tu compra"
              products={item.products}
              bgColor="bg-primary/5 dark:bg-primary/10"
              titleColor="text-primary"
              icon="local_mall"
            />
          </div>
        );
      case 'next_category':
        return (
          <div key="next-cat" className="mt-8 mb-4 animate-fade-in">
            <div 
              onClick={() => { 
                setFilter(item.categoryName); 
                setSearchQuery(''); 
                mainScrollRef.current?.scrollTo({top: 0, behavior: 'smooth'}); 
              }}
              className="relative w-full h-36 rounded-2xl overflow-hidden cursor-pointer group shadow-lg border border-white/10"
            >
              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" 
                style={{backgroundImage: `url("${item.bgImage}")`}}>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col justify-center px-8 text-white">
                <p className="text-xs font-bold text-white/70 uppercase tracking-widest mb-1 flex items-center gap-2">
                  <span className="w-8 h-px bg-white/50"></span> Sigue navegando
                </p>
                <h3 className="text-2xl font-black leading-none mb-2 shadow-black drop-shadow-md">Explora {item.categoryName}</h3>
                <div className="flex items-center gap-2 text-xs font-bold text-black bg-white w-fit px-4 py-2 rounded-full mt-2 transition-transform group-hover:translate-x-2">
                  Ver productos <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  }, [setFilter, setSearchQuery, mainScrollRef]);

  // Empty Favorites Hero
  if (isFavoritesView && !hasFavorites) {
    return (
      <>
        <div className="text-center py-10 animate-fade-in border-b border-black/5 dark:border-white/5 mb-8">
          <div className="w-20 h-20 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="material-symbols-outlined text-4xl text-red-500">favorite_border</span>
          </div>
          <h2 className="text-xl font-black text-text-main dark:text-white mb-2 leading-tight">
            Tu lista de deseos está vacía
          </h2>
          <p className="text-sm text-text-secondary dark:text-gray-400 max-w-xs mx-auto mb-8 leading-relaxed">
            ¿Viste algo que te gusta? ¡Guárdalo para que no se te escape!
          </p>
          
          <div className="flex items-center justify-center gap-2">
            <span className="h-px w-12 bg-gray-200 dark:bg-white/10"></span>
            <span className="text-[10px] font-bold uppercase text-gray-400">Te recomendamos explorar</span>
            <span className="h-px w-12 bg-gray-200 dark:bg-white/10"></span>
          </div>
        </div>
        
        {/* Show catalog below empty state */}
        {renderContent()}
      </>
    );
  }

  function renderContent() {
    if (loading) {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {[1, 2, 3, 4, 5, 6].map(i => <ProductSkeleton key={i} />)}
        </div>
      );
    }

    if (displayedProducts.length === 0) {
      if (crossCategoryMatches.length > 0) {
        return (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4 animate-fade-in">
            <div className="w-16 h-16 bg-black/5 dark:bg-white/5 rounded-full flex items-center justify-center mb-3">
              <span className="material-symbols-outlined text-3xl text-gray-400">search_off</span>
            </div>
            <p className="font-bold text-lg text-text-main dark:text-white">No encontramos &quot;{searchQuery}&quot; en {filter}</p>
            <p className="text-sm text-text-secondary dark:text-gray-400 mb-6">Pero encontramos resultados en otras secciones:</p>
            
            <div className="w-full space-y-3 mb-8">
              {Object.entries(crossCategoryMatches.reduce((acc, product) => {
                acc[product.category] = (acc[product.category] || 0) + 1;
                return acc;
              }, {} as Record<string, number>)).map(([catName, count]) => (
                <button 
                  key={catName}
                  onClick={() => handleSelectFilter(catName)}
                  className="w-full bg-surface-light dark:bg-surface-dark border border-black/5 dark:border-white/5 p-4 rounded-xl flex justify-between items-center shadow-sm hover:border-primary/50 transition-colors group"
                >
                  <span className="font-bold text-text-main dark:text-white flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">arrow_forward</span>
                    Ver en {catName}
                  </span>
                  <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-1 rounded-full">{count} encontrados</span>
                </button>
              ))}
            </div>
          </div>
        );
      }

      return (
        <div className="flex flex-col items-center justify-center py-16 text-center px-4 animate-fade-in">
          <div className="w-20 h-20 mb-4 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center">
            <span className="material-symbols-outlined text-5xl text-gray-400">inventory_2</span>
          </div>
          {filter !== 'Todos' ? (
            <>
              <p className="font-bold text-xl text-text-main dark:text-white mb-2">
                No hay productos de &quot;{filter}&quot;
              </p>
              <p className="text-sm text-text-secondary dark:text-gray-400 mb-6 max-w-sm">
                Esta categoría todavía no tiene productos disponibles.
              </p>
            </>
          ) : (
            <>
              <p className="font-bold text-xl text-text-main dark:text-white mb-2">
                Catálogo vacío
              </p>
              <p className="text-sm text-text-secondary dark:text-gray-400 mb-6 max-w-sm">
                Pronto tendrás productos disponibles aquí.
              </p>
            </>
          )}
          
          {filter !== 'Todos' && allProducts.length > 0 && (
            <div className="w-full max-w-md mb-6">
              <p className="text-sm font-bold text-primary mb-3 flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-sm">auto_awesome</span>
                Explora otras categorías
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {Array.from(new Set(allProducts.map(p => p.category)))
                  .filter(cat => cat !== filter)
                  .slice(0, 5)
                  .map(cat => (
                    <button
                      key={cat}
                      onClick={() => handleSelectFilter(cat)}
                      className="px-4 py-2 bg-surface-light dark:bg-surface-dark border border-black/10 dark:border-white/10 rounded-full text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors"
                    >
                      {cat}
                    </button>
                  ))}
              </div>
            </div>
          )}
          
          {allProducts.length > 0 && filter === 'Todos' && (
            <div className="w-full max-w-md">
              <p className="text-sm font-bold text-primary mb-3 flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-sm">auto_awesome</span>
                Destacados
              </p>
              <div className="grid grid-cols-3 gap-3">
                {allProducts.slice(0, 3).map(p => (
                  <div
                    key={p.id}
                    onClick={() => handleNavigateToProduct(p.id)}
                    className="bg-surface-light dark:bg-surface-dark border border-black/10 dark:border-white/10 rounded-xl p-2 cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    <div className="aspect-square bg-gray-100 dark:bg-white/5 rounded-lg mb-2 overflow-hidden">
                      {p.images?.[0] ? (
                        <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="material-symbols-outlined text-gray-400">image</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs font-medium text-text-main dark:text-white truncate">{p.title}</p>
                    <p className="text-xs text-primary font-bold">${p.price?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {filter !== 'Todos' && (
            <button
              onClick={handleShowAllCatalog}
              className="mt-6 bg-primary text-white px-6 py-2.5 rounded-full text-sm font-bold shadow-lg hover:bg-orange-600 transition-colors"
            >
              Ver todo el catálogo
            </button>
          )}
        </div>
      );
    }

return (
    <Virtuoso
      data={feedItems}
      itemContent={(index, item) => renderFeedItem(item, index)}
      style={{ height: '100%' }}
    />
  );
  }

  return (
    <>
      {renderContent()}
      
      {hasMore && !loading && !searchQuery && displayedProducts.length > 0 && (
        <div className="flex justify-center py-6">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="bg-surface-light dark:bg-surface-dark border border-black/10 dark:border-white/10 px-8 py-2.5 rounded-full text-sm font-bold shadow-md hover:bg-black/5 dark:hover:bg-white/5 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
          >
            {loadingMore ? (
              <>
                <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                Cargando...
              </>
            ) : 'Ver más productos'}
          </button>
        </div>
      )}
    </>
  );
});

CatalogFeed.displayName = 'CatalogFeed';
